// Base API client with JWT token support
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

class APIClient {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Get the current access token from localStorage
  getAccessToken() {
    return localStorage.getItem('accessToken');
  }

  // Make an authenticated request with JWT token
  async makeAuthenticatedRequest(endpoint, options = {}) {
    const token = this.getAccessToken();
    
    if (!token) {
      throw new Error('No access token available. Please log in again.');
    }

    const url = `${this.baseURL}${endpoint}`;
    
    // Don't set Content-Type for FormData, let the browser set it with boundary
    const defaultHeaders = {
      'Authorization': `Bearer ${token}`,
    };
    
    // Only set Content-Type if not sending FormData
    if (!(options.body instanceof FormData)) {
      defaultHeaders['Content-Type'] = 'application/json';
    }
    
    const config = {
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
      ...options,
    };

    console.log('Making authenticated request:', {
      url,
      method: config.method,
      headers: config.headers,
      hasBody: !!config.body,
      bodyType: typeof config.body
    });

    try {
      const response = await fetch(url, config);
      
      // Handle 401 Unauthorized - token might be expired
      if (response.status === 401) {
        // Clear stored token and redirect to login
        localStorage.removeItem('accessToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
        throw new Error('Session expired. Please log in again.');
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      // Handle 204 No Content responses (like DELETE)
      if (response.status === 204) {
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('Authenticated API request failed:', error);
      throw error;
    }
  }

  // Make a public request without authentication
  async makePublicRequest(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      // Handle 204 No Content responses (like DELETE)
      if (response.status === 204) {
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('Public API request failed:', error);
      throw error;
    }
  }
}

export default new APIClient();
